import type { StatMeta } from "./types";

export const stats: StatMeta[] = [
  {
    value: "Millions of",
    suffix: "",
    label: "Real IPs",
    description: "Ethically sourced residential & mobile IPs",
    decimals: 0,
  },
  {
    value: 195,
    suffix: "+",
    label: "Countries",
    description: "Global coverage with city & ASN targeting",
    decimals: 0,
  },
  {
    suffix: "%",
    label: "Uptime",
    description: "Independently monitored by BetterStack",
    decimals: 3,
  },
  {
    value: 0.6,
    suffix: "s",
    label: "Avg Response",
    description: "Ultra-low latency across all proxy types",
    decimals: 1,
    prefix: "<",
  },
];

// Height of the animation canvas / tab panel, in px. All particle geometry is
// proportional to this value, so the four scenes rescale automatically.
export const CANVAS_H = 360;

/* Secondary accent (orange), flipping to the deep step in light via
   --glow-yellow. This used to be 90% foreground: in light that made the
   sliding underline a near-black bar, and its long fades read as a shadow
   smeared under the active tab rather than as an accent. */
export const TAB_ACCENT = "rgb(var(--glow-yellow))";
// Horizontal — runs along the active tab's bottom edge in the tab row. The
// fades are kept short so it reads as an underline, not a soft glow.
export const TAB_ACCENT_GRADIENT = `linear-gradient(90deg, transparent 0%, ${TAB_ACCENT} 12%, ${TAB_ACCENT} 88%, transparent 100%)`;

export const PC = 200;
export const BR: [number, number, number] = [247, 247, 248];
/** Secondary accent (orange) — mirrors --accent-orange / YELLOW. */
export const YEL: [number, number, number] = [255, 138, 91];
export const GRN: [number, number, number] = [91, 140, 255];
export const RED: [number, number, number] = [248, 113, 113];
export const SP = 0.028;
export const DMP = 0.84;
export const WHITE: [number, number, number] = [255, 255, 255];
/** Secondary ink — the quiet mark every canvas uses for filler/atmosphere, so
 *  the strong ink and the two accents stay meaningful. */
export const DIM: [number, number, number] = [156, 158, 168];

type Rgb = [number, number, number];

/** Canvas drawings can't resolve CSS variables, so each canvas picks its
 *  palette from the visitor's color scheme (see `usePrefersLight`). The light
 *  values mirror the light-theme tokens in `globals.css` (`--foreground`
 *  #0a2540, slate-2 #8792a2, `--glow-yellow`, `--glow-green`). */
export function canvasPalette(prefersLight: boolean): {
  br: Rgb;
  white: Rgb;
  dim: Rgb;
  yel: Rgb;
  grn: Rgb;
  red: Rgb;
} {
  return prefersLight
    ? {
        /* Ink is the light theme's navy foreground, not a neutral near-black —
           a cold #131316 read as dirt against the navy/blurple light palette. */
        br: [10, 37, 64],
        white: [10, 37, 64],
        dim: [135, 146, 162],
        /* Secondary accent — the deep orange step (--glow-yellow in light).
           This used to be blue, a leftover from the cyan era when the light
           theme merged both accents; that made every canvas that alternates
           yel/grn (the globe markers, the dot globe) read as one flat blue. */
        yel: [224, 106, 52],
        grn: [37, 99, 235],
        red: [220, 38, 38],
      }
    : { br: BR, white: WHITE, dim: DIM, yel: YEL, grn: GRN, red: RED };
}

/** Theme-aware `rgba()` builder for the stats canvases. The canvas alphas were
 *  tuned for white marks on a near-black canvas; the same alphas read too faint
 *  as dark marks on the light theme's white canvas. In light we lift alpha with
 *  a gamma curve — faint atmospheric marks (0.05 → ~0.12, 0.16 → ~0.30) gain the
 *  most, while already-solid marks stay put. Fully transparent stops stay 0.
 *  Dark is passed through unchanged. */
export function canvasRgba(prefersLight: boolean): (c: Rgb, a: number) => string {
  return (c, a) => {
    const alpha = !prefersLight || a <= 0 ? a : Math.min(1, Math.pow(a, 0.72) * 1.15);
    return `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha})`;
  };
}

export const UPTIME_COLS = 40;
export const UPTIME_ROWS = 5;
export const UPTIME_SERVICES = [
  "Mobile Proxies",
  "Web Services",
  "API Services",
  "Frontend",
  "Comm Broker",
];

// Incident segment indices: row * UPTIME_COLS + col
// Row 3 (4th row) col 35 → 155, leaving 4 green segments after it
export const UPTIME_INCIDENTS = new Set<number>([155]);

export const CITY_LATLNG: [number, number][] = [
  [0.71, 4.991], // New York
  [0.899, 0.0], // London
  [0.623, 2.438], // Tokyo
  [-0.592, 2.638], // Sydney
  [0.333, 1.272], // Mumbai
  [-0.41, 5.47], // São Paulo
  [0.525, 0.544], // Cairo
  [0.024, 1.811], // Singapore
  [0.974, 0.656], // Moscow
  [0.763, 4.897], // Toronto
  [0.113, 0.059], // Lagos
  [0.697, 2.031], // Beijing
  [-0.604, 5.264], // Buenos Aires
  [0.916, 0.234], // Berlin
  [0.44, 0.965], // Dubai
  [0.854, 0.042], // Paris
  [0.595, 4.22], // Los Angeles
  [-0.592, 0.321], // Cape Town
  [0.716, 0.504], // Istanbul
  [0.656, 2.217], // Seoul
  [0.731, 4.754], // Chicago
  [0.241, 1.754], // Bangkok
  [0.339, 4.554], // Mexico City
  [-0.023, 0.642], // Nairobi
  [-0.108, 1.864], // Jakarta
];

export const SPHERE_DATA: { lat: number; lng: number; isCity: boolean }[] = (() => {
  const d: { lat: number; lng: number; isCity: boolean }[] = [];
  for (const [lat, lng] of CITY_LATLNG) d.push({ lat, lng, isCity: true });
  const n = PC - CITY_LATLNG.length;
  const ga = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const sinLat = 1 - (i / (n - 1)) * 2;
    d.push({
      lat: Math.asin(Math.max(-1, Math.min(1, sinLat))),
      lng: (((ga * i) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI),
      isCity: false,
    });
  }
  return d;
})();

export const IP_CLUSTERS = [
  { rx: 0.5, ry: 0.5, sx: 0.2, sy: 0.16, rotDir: 1 },
  { rx: 0.22, ry: 0.35, sx: 0.14, sy: 0.12, rotDir: -1 },
  { rx: 0.78, ry: 0.65, sx: 0.14, sy: 0.12, rotDir: 1 },
  { rx: 0.65, ry: 0.25, sx: 0.12, sy: 0.11, rotDir: -1 },
  { rx: 0.32, ry: 0.72, sx: 0.11, sy: 0.1, rotDir: 1 },
];
